import { RefreshFromWorkfrontButton } from '@/components/work-orders/RefreshFromWorkfrontButton'
import { FromWorkfrontBadge, isFromWorkfront, WaitingForMappingBadge } from '@/components/work-orders/WorkfrontBadges'
import type { WorkOrder } from '@/domain/work-order.types'
import { useRefreshFromWorkfront } from '@/hooks/useWorkOrderSync'
import { useWorkOrderDetail } from '@/hooks/useWorkOrders'

/**
 * The header's Workfront line: the badge marking the title (and the other owned values) as
 * Workfront's, and, while a mapping error holds the order, what it is waiting for. Nothing
 * on a manual order.
 */
export function WorkOrderWorkfrontBadges({ order }: { order: Pick<WorkOrder, 'source' | 'externalHold'> }) {
	if (!isFromWorkfront(order)) return null
	return (
		<span className="flex flex-wrap items-center gap-2">
			<FromWorkfrontBadge />
			{order.externalHold ? <WaitingForMappingBadge titles={order.externalHold.titles} /> : null}
		</span>
	)
}

/**
 * The header's refresh action, wired to the run. Shown only when the detail read says the
 * caller may refresh (a Workfront order, the refresh enabled, the caller its PM or an admin).
 */
export function WorkOrderRefreshFromWorkfront({ workOrderId }: { workOrderId: string }) {
	const { data: allowed = false } = useWorkOrderDetail(
		workOrderId,
		(detail) => detail.refreshFromWorkfrontAllowed === true,
	)
	if (!allowed) return null
	return <RefreshAction workOrderId={workOrderId} />
}

function RefreshAction({ workOrderId }: { workOrderId: string }) {
	const { refresh, isRefreshing, alreadySyncing, heldReason } = useRefreshFromWorkfront(workOrderId)
	return (
		<RefreshFromWorkfrontButton
			isRefreshing={isRefreshing}
			alreadySyncing={alreadySyncing}
			alreadySyncingReason={heldReason}
			onRefresh={refresh}
		/>
	)
}
